import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  where,
  type DocumentData
} from 'firebase/firestore';
import { db } from '../backend/firebase';
import { MediaHistoryItem } from '../types';

const PAGE_SIZE = 30;
const STATUS_FILTER = ['active', 'published'];

export interface UserMediaCounts {
  total: number;
  minis: number;
  longs: number;
  posts: number;
}

function mapMedia(docSnap: QueryDocumentSnapshot<DocumentData>): MediaHistoryItem {
  const data = docSnap.data();
  const createdAt = data.createdAt;
  const date = createdAt?.toDate?.();
  const numericCount = (value: unknown) => {
    const count = Number(value);
    return Number.isFinite(count) && count > 0 ? count : 0;
  };
  return {
    ...data,
    id: docSnap.id,
    type: normalizeType(data.type),
    mediaUrl: data.mediaUrl || data.storagePath || '',
    thumbnailUrl: data.thumbnailUrl || data.thumbnailStoragePath || '',
    uploadDate: date instanceof Date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : '',
    views: numericCount(data.viewCount ?? data.views),
    likes: numericCount(data.likeCount ?? data.likesCount ?? data.likes),
    userId: data.ownerUid || data.uploaderId || data.userId || '',
    createdAt
  } as MediaHistoryItem;
}

function normalizeType(value: unknown): MediaHistoryItem['type'] {
  const type = String(value || '').toLowerCase();
  if (['short', 'shorts', 'minis', 'reel', 'reels'].includes(type)) return 'short';
  if (['long', 'longs', 'video', 'videos'].includes(type)) return 'long';
  if (['photo', 'photos', 'image', 'images', 'post', 'posts'].includes(type)) return 'photo';
  return 'live';
}

export function useUserMedia(uid: string | undefined) {
  const [items, setItems] = useState<MediaHistoryItem[]>([]);
  const [counts, setCounts] = useState<UserMediaCounts>({ total: 0, minis: 0, longs: 0, posts: 0 });
  const [loading, setLoading] = useState(Boolean(uid));
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const lastDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const pageCursorsRef = useRef<QueryDocumentSnapshot<DocumentData>[]>([]);
  const firstPageIdsRef = useRef<Set<string>>(new Set());
  const loadingRef = useRef(false);

  useEffect(() => {
    setItems([]);
    setCounts({ total: 0, minis: 0, longs: 0, posts: 0 });
    lastDocRef.current = null;
    pageCursorsRef.current = [];
    firstPageIdsRef.current = new Set();
    setError(null);
    if (!uid) {
      setLoading(false);
      setHasMore(false);
      return;
    }

    setLoading(true);
    const mediaRef = collection(db, 'media');
    const baseConstraints = [
      where('ownerUid', '==', uid),
      where('status', 'in', STATUS_FILTER)
    ];
    const firstPageQuery = query(
      mediaRef,
      ...baseConstraints,
      orderBy('createdAt', 'desc'),
      limit(PAGE_SIZE)
    );

    const countQuery = (types?: string[]) => query(
      mediaRef,
      ...baseConstraints,
      ...(types ? [where('type', 'in', types)] : [])
    );
    void Promise.all([
      getCountFromServer(countQuery()),
      getCountFromServer(countQuery(['short', 'shorts', 'minis', 'reel', 'reels'])),
      getCountFromServer(countQuery(['long', 'longs', 'video', 'videos'])),
      getCountFromServer(countQuery(['photo', 'photos', 'image', 'images', 'post', 'posts']))
    ]).then(([total, minis, longs, posts]) => {
      setCounts({
        total: total.data().count,
        minis: minis.data().count,
        longs: longs.data().count,
        posts: posts.data().count
      });
    }).catch(countError => {
      console.error('Could not count profile media:', countError);
      setError(countError instanceof Error ? countError : new Error('Could not count profile media.'));
    });

    return onSnapshot(firstPageQuery, snapshot => {
      const previousFirstPageIds = firstPageIdsRef.current;
      const firstPage = snapshot.docs.map(mapMedia);
      const firstPageIds = new Set(firstPage.map(item => item.id));
      firstPageIdsRef.current = firstPageIds;
      pageCursorsRef.current[0] = snapshot.docs[snapshot.docs.length - 1];
      if (pageCursorsRef.current.length <= 1) lastDocRef.current = pageCursorsRef.current[0] || null;
      setItems(previous => {
        const tail = previous.filter(item => !previousFirstPageIds.has(item.id) && !firstPageIds.has(item.id));
        return [...firstPage, ...tail];
      });
      setHasMore(snapshot.size === PAGE_SIZE);
      setError(null);
      setLoading(false);
    }, snapshotError => {
      setError(snapshotError);
      setLoading(false);
    });
  }, [uid]);

  const loadMore = useCallback(async () => {
    if (!uid || !lastDocRef.current || loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoadingMore(true);
    setError(null);
    try {
      const nextQuery = query(
        collection(db, 'media'),
        where('ownerUid', '==', uid),
        where('status', 'in', STATUS_FILTER),
        orderBy('createdAt', 'desc'),
        startAfter(lastDocRef.current),
        limit(PAGE_SIZE)
      );
      const snapshot = await getDocs(nextQuery);
      const nextCursor = snapshot.docs[snapshot.docs.length - 1];
      if (nextCursor) {
        lastDocRef.current = nextCursor;
        pageCursorsRef.current.push(nextCursor);
      }
      setItems(previous => {
        const seen = new Set(previous.map(item => item.id));
        return [...previous, ...snapshot.docs.map(mapMedia).filter(item => !seen.has(item.id))];
      });
      setHasMore(snapshot.size === PAGE_SIZE);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError : new Error('Could not load more profile media.'));
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, uid]);

  return { items, counts, loading, loadingMore, hasMore, error, loadMore };
}
